import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Player } from '../../../types';

interface PlayerOrganizerPanelProps {
  player: Player;
  allPlayers: Player[];
  onSetSkill: (playerId: string, score: number) => void;
  onLinkFriend: (playerAId: string, playerBId: string) => void;
  onUnlinkFriend: (playerId: string) => void;
}

const GROUP_COLORS = [
  '#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#FF922B',
  '#CC5DE8', '#20C997', '#F06595', '#74C0FC', '#A9E34B',
];

function colorForGroup(groupId: string): string {
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = (hash * 31 + groupId.charCodeAt(i)) & 0xffffffff;
  }
  return GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length];
}

export const PlayerOrganizerPanel: React.FC<PlayerOrganizerPanelProps> = ({
  player,
  allPlayers,
  onSetSkill,
  onLinkFriend,
  onUnlinkFriend,
}) => {
  const [score, setScore] = useState<number>(player.organizerSkillScore ?? 0);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'skill' | 'link'>('skill');

  const handleScore = (n: number) => {
    setScore(n);
    onSetSkill(player.id, n);
  };

  const groupColor = player.friendGroupId ? colorForGroup(player.friendGroupId) : null;
  const groupmates = player.friendGroupId
    ? allPlayers.filter(p => p.id !== player.id && p.friendGroupId === player.friendGroupId)
    : [];
  const linkablePlayers = allPlayers.filter(p => p.id !== player.id);

  const modal = open ? ReactDOM.createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#1E2327',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '14px',
          padding: '20px',
          width: '100%',
          maxWidth: '340px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#FFFFFF' }}>
              Poner puntaje
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
              {player.name}
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#B0BEC5',
              fontSize: '16px',
              fontFamily: 'inherit',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {(['skill', 'link'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                flex: 1,
                padding: '6px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: 'inherit',
                background: tab === t ? 'rgba(0,230,118,0.15)' : 'rgba(255,255,255,0.05)',
                color: tab === t ? '#00E676' : '#6B7280',
                transition: 'all 0.15s',
              }}
            >
              {t === 'skill' ? '🔒 Nivel privado' : '🔗 Juegan juntos'}
            </button>
          ))}
        </div>

        {/* Skill tab */}
        {tab === 'skill' && (
          <div>
            <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '10px' }}>
              Seleccioná un nivel del 1 al 10
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <button
                  key={n}
                  data-testid={`organizer-skill-btn-${n}`}
                  onClick={() => handleScore(n)}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    border: score === n ? '2px solid #00E676' : '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    background: score === n ? 'rgba(0,230,118,0.2)' : 'rgba(255,255,255,0.06)',
                    color: score === n ? '#00E676' : '#B0BEC5',
                    transition: 'all 0.12s',
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
            {score > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#B0BEC5' }}>
                  Nivel actual: <strong style={{ color: '#00E676' }}>{score}</strong>
                </span>
                <button
                  data-testid="organizer-skill-clear"
                  onClick={() => handleScore(0)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,23,68,0.3)',
                    background: 'rgba(255,23,68,0.1)',
                    color: '#FF1744',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontFamily: 'inherit',
                    fontWeight: 600,
                  }}
                >
                  Quitar
                </button>
              </div>
            )}
          </div>
        )}

        {/* Link tab */}
        {tab === 'link' && (
          <div>
            <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '10px' }}>
              Seleccioná con quién juega habitualmente
            </div>
            {groupColor && groupmates.length > 0 && (
              <div style={{
                padding: '8px 10px',
                background: groupColor + '18',
                border: `1px solid ${groupColor}44`,
                borderRadius: '8px',
                marginBottom: '8px',
                fontSize: '12px',
                color: groupColor,
                fontWeight: 600,
              }}>
                👥 Vinculado con: {groupmates.map(p => p.name).join(', ')}
              </div>
            )}
            {player.friendGroupId && (
              <button
                data-testid="organizer-unlink-btn"
                onClick={() => { onUnlinkFriend(player.id); setOpen(false); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 10px', marginBottom: '8px',
                  background: 'rgba(255,23,68,0.08)', border: '1px solid rgba(255,23,68,0.2)',
                  borderRadius: '8px', color: '#FF5252', cursor: 'pointer',
                  fontSize: '12px', fontFamily: 'inherit', fontWeight: 600,
                }}
              >
                Desvincular
              </button>
            )}
            <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {linkablePlayers.map(p => {
                const sameGroup = player.friendGroupId && p.friendGroupId === player.friendGroupId;
                return (
                  <button
                    key={p.id}
                    data-testid={`organizer-link-player-${p.id}`}
                    onClick={() => { onLinkFriend(player.id, p.id); setOpen(false); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '8px 10px',
                      background: sameGroup ? 'rgba(76,175,80,0.12)' : 'rgba(255,255,255,0.04)',
                      border: sameGroup ? '1px solid rgba(76,175,80,0.3)' : '1px solid transparent',
                      borderRadius: '6px',
                      color: sameGroup ? '#81C784' : '#CFD8DC',
                      cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit',
                    }}
                  >
                    {sameGroup ? '✓ ' : ''}{p.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {/* Trigger button */}
      <button
        data-testid="organizer-skill-badge"
        onClick={e => { e.stopPropagation(); setOpen(true); }}
        title="Poner puntaje privado al jugador"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '3px 8px',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.15)',
          background: score > 0 ? 'rgba(0,230,118,0.12)' : 'rgba(255,255,255,0.06)',
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: 600,
          color: score > 0 ? '#00E676' : '#6B7280',
          fontFamily: 'inherit',
          transition: 'all 0.15s',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: '10px' }}>🔒</span>
        <span data-testid="organizer-skill-value">{score > 0 ? score : 'Puntuar'}</span>
        {groupColor && (
          <span style={{ color: groupColor, fontSize: '10px' }}>👥</span>
        )}
      </button>

      {modal}
    </>
  );
};
